import { createStore, createEvent, createEffect, combine } from 'effector';
import update from 'immutability-helper';
import request from '@helpers/request';
import api from '@constants/endpoints';
import { PaginationProps } from 'antd/lib/pagination';
import createPaginationStore from './pagination';
import createPreloadersStore from './preloaders';
import createFiltersStore from './filters';
import removeUndefinedFilters from '@helpers/removeUndefinedFilters';
import IComment from '../interfaces/IComment';
import { block, unblock } from './comments';
import { blockCommentHandler } from './post';
import localeWeight, { LOCALE } from '@stores/localeWeight';

export const reset = createEvent('Spam store reset');

const defaultFilters = {
  sort: 'lastClaimDate,desc',
  needChecked: false,
};

export const getList = createEffect({
  name: 'getList',
  async handler(params?: { pagination?: PaginationProps; filters?: Record<string, any> }) {
    const paginationParams: PaginationProps = {
      ...pagination.getState(),
      ...params?.pagination,
    };

    let filtersParams = filters.getState();
    if (params?.filters) {
      setFilters(params.filters);
      filtersParams = params.filters;
      paginationParams.current = 1;
    }

    const { current } = paginationParams;

    const response = await request(api.spam.getComments, {
      params: {
        paged: true,
        size: localStorage.getItem('spamCommentsPageSize'),
        page: (current || 1) - 1,
        ...defaultFilters,
        ...removeUndefinedFilters({
          ...filtersParams,
          locale: localeWeight.getState() === LOCALE.ALL ? undefined : localeWeight.getState(),
        }),
      },
    });
    return response;
  },
});

export const getById = createEffect({
  name: 'getById',
  async handler(id: IComment['id']) {
    setFilters({ id });

    if (!id) {
      getList();
      return null;
    } else {
      const response = await request(api.spam.getComments, { params: { commentId: id } });
      if (!response.data.body) return null;
      return response;
    }
  },
});

const { pagination, setPagination } = createPaginationStore({
  getList,
  listName: 'spamComments',
  resetEvent: reset,
  bodyPath: ['data', 'body'],
});

export { setPagination };

const preloaders = createPreloadersStore([getList, block, unblock, getById]);

const { filters, setFilters } = createFiltersStore({
  listName: 'spamComments',
  deaultValues: defaultFilters,
  resetEvent: reset,
});
export { setFilters };

export const updateListHandler = (
  state: IComment[],
  {
    result: { data },
    params: { commentId },
  }: { result: { data: IComment }; params: { commentId: IComment['id'] } },
) => {
  const index = state.findIndex((c) => c.id === commentId);
  const filtersParams = filters.getState();
  const { needChecked } = filtersParams;
  if (index >= 0 && !needChecked) {
    const updatedState: any = update(state, {
      $splice: [[index, 1]],
    });
    return updatedState;
  } else {
    return state;
  }
};

const list = createStore<IComment[]>([])
  .on(getList.done, (state, { result }) => result.data.body.content)
  .on(getById.done, (state, { result }) => result?.data.body.content || [])
  .on(block.done, updateListHandler)
  .on(unblock.done, updateListHandler)
  .reset(reset);

const spamComments = combine({ preloaders, list, pagination, filters });

export default spamComments;
